import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  Paper,
  Modal,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Button,
  Stack,
  InputAdornment,
  Divider,
  Badge,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import toast from "react-hot-toast";
import UseOnlineStatus from "../../common/UseOnlineStatus";

const token = localStorage.getItem("token");

const vehicleFilterOptions = [
  { label: "Any vehicles", value: "" },
  { label: "With vehicles", value: "with" },
  { label: "Empty groups", value: "without" },
];

const modalStyle = {
  position: "absolute",
  top: "24px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "80%",
  maxWidth: 1200,
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

const packageModalStyle = { ...modalStyle, maxWidth: "95%", width: "95%" };
const createGroupModalStyle = { ...modalStyle, width: "90%", maxWidth: 520 };

const emptyNewGroup = {
  name: "",
  code: "",
  cityId: "",
  hubId : "",
  seatingCapacity: "",
  securityDeposit: "",
};

const headCells = [
  { id: "id", label: "Group ID", numeric: true },
  { id: "name", label: "Name", numeric: false },
  { id: "type", label: "Type", numeric: false },
  { id: "code", label: "Code", numeric: false },
  { id: "cityId", label: "City", numeric: false },
  { id: "securityDeposit", label: "Security deposit", numeric: true },
  { id: "vehicles", label: "Vehicles", numeric: true },
  { id: "daily", label: "Daily rental packages", numeric: true },
  { id: "subscription", label: "Subscription rental packages", numeric: true },
];

export default function Vehiclegroupsdashboard() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const { isSlowConnection } = UseOnlineStatus();
  const MINIMUM_DAY_COUNT = 7
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const vehicleTypeList = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.vehicleType;
  // filters
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [getAllHubsListData, setGetAllHubsListData] = useState(null);

  // sorting
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("desc");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openModal, setOpenModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupVehicles, setGroupVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const [packageModal, setPackageModal] = useState({ open: false, kind: null });
  const [packageGroup, setPackageGroup] = useState(null);
  const [packageRows, setPackageRows] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [subscriptionPackageAddAllowed, setSubscriptionPackageAddAllowed] =
    useState(false);
  const [subscriptionVehicleCount, setSubscriptionVehicleCount] = useState(0);

  const [editingPackageId, setEditingPackageId] = useState(null);
  const [packageEditForm, setPackageEditForm] = useState({});
  const [addingPackage, setAddingPackage] = useState(false);
  const [newPackageForm, setNewPackageForm] = useState({});
  const [savingPackage, setSavingPackage] = useState(false);

  useEffect(() => {
    if (isSlowConnection && packageModal.kind === "subscription") {
      setEditingPackageId(null);
      setPackageEditForm({});
      setAddingPackage(false);
      setNewPackageForm({});
    }
  }, [isSlowConnection, packageModal.kind]);

  const [dailyPackages, setDailyPackages] = useState([]);
  const [subPackages, setSubPackages] = useState([]);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [newGroup, setNewGroup] = useState(emptyNewGroup);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const [cities, setCities] = useState({});

  let vehicleStatus = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.vehicleStatus;

  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `${token}`,
  };

  const getAllHubsList = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/hubs?limit=1000`);
      const data = await response.json();
      if (data?.statusCode === "0100")
        setGetAllHubsListData(data?.entities?.rows);
    } catch (error) {
      console.error("Error fetching City Names:", error);
    }
  };

  const getVehicleGroups = async (cityId) => {
    setLoadingGroups(true);
    try {
      let url = `${apiUrl}/api/vehiclegroups?limit=1000`;
      if (cityId !== "" && cityId !== undefined && cityId !== null) {
        url += `&cityId=${cityId}`;
      }
      const response = await fetch(url, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setGroups(data?.entities?.rows || []);
      } else {
        toast.error(
          data?.msg || data?.message || "Failed to fetch vehicle groups",
        );
      }
    } catch (error) {
      console.error("Error fetching vehicle groups:", error);
      toast.error("Error fetching vehicle groups");
    } finally {
      setLoadingGroups(false);
    }
  };

  // Generic limit/offset pagination over { entities: { count, rows } } endpoints.
  const fetchAllPaginated = async (path) => {
    const limit = 100;
    let offset = 0;
    let all = [];
    let total = 0;

    do {
      const sep = path.includes("?") ? "&" : "?";
      const response = await fetch(
        `${apiUrl}${path}${sep}limit=${limit}&offset=${offset}`,
        { method: "GET", headers: authHeaders },
      );
      const data = await response.json();
      if (data?.statusCode !== "0100") {
        toast.error(data?.msg || data?.message || `Failed to fetch ${path}`);
        break;
      }
      const rows = data?.entities?.rows || [];
      all = all.concat(rows);
      total = data?.entities?.count || 0;
      offset += limit;
    } while (offset < total);

    return all;
  };

  const getAllVehicles = async () => {
    setLoadingVehicles(true);
    try {
      return await fetchAllPaginated("/api/vehicles");
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      toast.error("Error fetching vehicles");
      return [];
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    getVehicleGroups(city);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  useEffect(() => {
    (async () => {
      setDailyPackages(await fetchAllPaginated("/api/rentalpackages"));
      setSubPackages(await fetchAllPaginated("/api/subscriptionpackages"));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCityNames = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/cities/indexify`);
      const data = await response.json();
      if (data) setCities(data);
    } catch (error) {
      console.error("Error fetching city names:", error);
    }
  };

  useEffect(() => {
    getCityNames();
    getAllHubsList();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search, vehicleFilter, rowsPerPage]);

  const handleRowClick = async (group) => {
    setSelectedGroup(group);
    setOpenModal(true);
    setGroupVehicles([]);
    const allVehicles = await getAllVehicles();
    setGroupVehicles(
      allVehicles.filter((v) => v?.vehicleGroupId === group?.id),
    );
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedGroup(null);
    setGroupVehicles([]);
  };

  // Both package endpoints expose groupId — that's the join key back to a vehicle group.
  const handleOpenPackages = async (group, kind) => {
    setPackageGroup(group);
    setPackageRows([]);
    setPackageModal({ open: true, kind });
    setLoadingPackages(true);
    setSubscriptionPackageAddAllowed(false);
    setSubscriptionVehicleCount(0);
    try {
      const path =
        kind === "daily" ? "/api/rentalpackages" : "/api/subscriptionpackages";
      const [all, vehicles] = await Promise.all([
        fetchAllPaginated(path),
        kind === "subscription" ? fetchAllPaginated("/api/vehicles") : [],
      ]);
      setPackageRows(
        all.filter((p) => String(p?.groupId) === String(group?.id)),
      );

      if (kind === "subscription") {
        const groupVehicles = vehicles.filter(
          (vehicle) => String(vehicle?.vehicleGroupId) === String(group?.id),
        );
        setSubscriptionVehicleCount(groupVehicles.length);
        setSubscriptionPackageAddAllowed(
          groupVehicles.some(
            (vehicle) =>
              vehicle?.isSubscriptionEnabled === true ||
              vehicle?.isSubscriptionEnabled === 1 ||
              String(vehicle?.isSubscriptionEnabled).toLowerCase() === "true",
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Error fetching packages");
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleClosePackages = () => {
    setPackageModal({ open: false, kind: null });
    setPackageGroup(null);
    setPackageRows([]);
    setEditingPackageId(null);
    setPackageEditForm({});
    setAddingPackage(false);
    setNewPackageForm({});
  };

  const packagePath =
    packageModal.kind === "daily"
      ? "/api/rentalpackages"
      : "/api/subscriptionpackages";

  // Refetches the modal's rows for the current group AND the global
  // daily/subscription lists that back the table's count columns.
  const refreshPackagesAfterChange = async () => {
    const [all, dailyAll, subAll] = await Promise.all([
      fetchAllPaginated(packagePath),
      fetchAllPaginated("/api/rentalpackages"),
      fetchAllPaginated("/api/subscriptionpackages"),
    ]);
    setPackageRows(all.filter((p) => p?.groupId === packageGroup?.id));
    setDailyPackages(dailyAll);
    setSubPackages(subAll);
  };

  const startEditPackage = (pkg) => {
    if (packageModal.kind === "subscription" && isSlowConnection) {
      return toast.error("Subscription packages cannot be edited on a slow network");
    }
    if (editingPackageId || addingPackage) return;
    setEditingPackageId(pkg.id);
    setPackageEditForm({ ...pkg });
  };

  const cancelEditPackage = () => {
    setEditingPackageId(null);
    setPackageEditForm({});
  };

  const handlePackageFieldChange = (field, value) => {
    setPackageEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEditPackage = async () => {
    if (packageModal.kind === "subscription" && isSlowConnection) {
      return toast.error("Subscription packages cannot be edited on a slow network");
    }
    const isDaily = packageModal.kind === "daily";
    const payload = isDaily
      ? {
          id: packageEditForm.id,
          name: packageEditForm.name,
          totalFreeKm: Number(packageEditForm.totalFreeKm),
          kmPerHr: Number(packageEditForm.kmPerHr),
          costPerKm: Number(packageEditForm.costPerKm),
          costPerHr: Number(packageEditForm.costPerHr),
          extraKmCost: Number(packageEditForm.extraKmCost),
          extraHrCost: Number(packageEditForm.extraHrCost),
          weekdayTariff: Number(packageEditForm.weekdayTariff),
          weekendTariff: Number(packageEditForm.weekendTariff),
          packageType: packageEditForm.packageType ?? 0,
          groupId: packageGroup?.id,
        }
      : {
          id: packageEditForm.id,
          packageName: packageEditForm.packageName,
          minDayCount: MINIMUM_DAY_COUNT,
          dailyRateWeekly: Number(packageEditForm.dailyRateWeekly),
          dailyRateFortnightly: Number(packageEditForm.dailyRateFortnightly),
          dailyRateMonthly: Number(packageEditForm.dailyRateMonthly),
          kmPerDayWeekly: Number(packageEditForm.kmPerDayWeekly),
          kmPerDayFortnightly: Number(packageEditForm.kmPerDayFortnightly),
          kmPerDayMonthly: Number(packageEditForm.kmPerDayMonthly),
          extraKmCost: Number(packageEditForm.extraKmCost),
          extraHrCost: 0,
          groupId: packageGroup?.id,
          cityId: packageGroup?.cityId,
          hubId : getAllHubsListData && getAllHubsListData?.find(hub => hub?.cityId === packageGroup?.cityId)?.id
        };

    setSavingPackage(true);
    try {
      const response = await fetch(`${apiUrl}${packagePath}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data?.statusCode === "0100") {
        toast.success("Package updated successfully");
        cancelEditPackage();
        await refreshPackagesAfterChange();
      } else {
        toast.error(data?.msg || data?.message || "Failed to update package");
      }
    } catch (error) {
      console.error("Error updating package:", error);
      toast.error("Error updating package");
    } finally {
      setSavingPackage(false);
    }
  };

  const emptyDailyPackageForm = {
    name: "",
    totalFreeKm: "",
    kmPerHr: "",
    costPerKm: "",
    costPerHr: "",
    extraKmCost: "",
    extraHrCost: "",
    weekdayTariff: "",
    weekendTariff: "",
  };

  const emptySubPackageForm = {
    packageName: "",
    minDayCount: "",
    dailyRateWeekly: "",
    dailyRateFortnightly: "",
    dailyRateMonthly: "",
    kmPerDayWeekly: "",
    kmPerDayFortnightly: "",
    kmPerDayMonthly: "",
    extraKmCost: "",
    extraHrCost: "",
  };

  const startAddPackage = () => {
    if (packageModal.kind === "subscription" && isSlowConnection) {
      return toast.error("Subscription packages cannot be added on a slow network");
    }
    if (editingPackageId || addingPackage) return;
    setNewPackageForm(
      packageModal.kind === "daily"
        ? emptyDailyPackageForm
        : emptySubPackageForm,
    );
    setAddingPackage(true);
  };

  const cancelAddPackage = () => {
    setAddingPackage(false);
    setNewPackageForm({});
  };

  const handleNewPackageChange = (field, value) => {
    setNewPackageForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveNewPackage = async () => {
    const isDaily = packageModal.kind === "daily";
    if (!isDaily && isSlowConnection) {
      return toast.error("Subscription packages cannot be added on a slow network");
    }
    if (!isDaily && !subscriptionPackageAddAllowed) {
      return toast.error(
        "A subscription package requires one vehicle or at least one subscription-enabled vehicle in the group",
      );
    }
    if (isDaily && !newPackageForm.name?.trim()) {
      return toast.error("Package name is required");
    }
    if (!isDaily && !newPackageForm.packageName?.trim()) {
      return toast.error("Package name is required");
    }

    const payload = isDaily
      ? {
          name: newPackageForm.name,
          totalFreeKm: Number(newPackageForm.totalFreeKm) || 0,
          kmPerHr: Number(newPackageForm.kmPerHr) || 0,
          costPerKm: Number(newPackageForm.costPerKm) || 0,
          costPerHr: Number(newPackageForm.costPerHr) || 0,
          extraKmCost: Number(newPackageForm.extraKmCost) || 0,
          extraHrCost: Number(newPackageForm.extraHrCost) || 0,
          weekdayTariff: Number(newPackageForm.weekdayTariff) || 0,
          weekendTariff: Number(newPackageForm.weekendTariff) || 0,
          packageType: 0,
          groupId: packageGroup?.id,
          // cityId : packageGroup?.cityId
        }
      : {
          packageName: newPackageForm.packageName,
          minDayCount: MINIMUM_DAY_COUNT,
          dailyRateWeekly: Number(newPackageForm.dailyRateWeekly) || 0,
          dailyRateFortnightly:
            Number(newPackageForm.dailyRateFortnightly) || 0,
          dailyRateMonthly: Number(newPackageForm.dailyRateMonthly) || 0,
          kmPerDayWeekly: Number(newPackageForm.kmPerDayWeekly) || 0,
          kmPerDayFortnightly: Number(newPackageForm.kmPerDayFortnightly) || 0,
          kmPerDayMonthly: Number(newPackageForm.kmPerDayMonthly) || 0,
          extraKmCost: Number(newPackageForm.extraKmCost) || 0,
          extraHrCost: 0,
          groupId: packageGroup?.id,
          cityId: packageGroup?.cityId,
          hubId : getAllHubsListData && getAllHubsListData?.find(hub => hub?.cityId === packageGroup?.cityId)?.id
        };     

    setSavingPackage(true);
    try {
      const response = await fetch(`${apiUrl}${packagePath}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data?.statusCode === "0100") {
        toast.success("Package added successfully");
        cancelAddPackage();
        await refreshPackagesAfterChange();
      } else {
        toast.error(data?.msg || data?.message || "Failed to add package");
      }
    } catch (error) {
      console.error("Error adding package:", error);
      toast.error("Error adding package");
    } finally {
      setSavingPackage(false);
    }
  };

  const handleCopyPlate = (e, plate) => {
    e.stopPropagation();
    if (!plate) return;
    navigator.clipboard.writeText(plate);
    toast.success("License plate copied");
  };

  const countVehicles = (g) => {
    if (!g?.vehicleList) return 0;
    return Array.isArray(g.vehicleList) ? g.vehicleList.length : 1;
  };

  const countDailyPackages = (g) =>
    dailyPackages.filter((p) => p?.groupId === g?.id).length;

  const countSubscriptionPackages = (g) =>
    subPackages.filter((p) => p?.groupId === g?.id).length;

  const filtersActive = !!search.trim() || city !== "" || vehicleFilter !== "";

  const activeFilterCount =
    (search.trim() ? 1 : 0) +
    (city !== "" ? 1 : 0) +
    (vehicleFilter !== "" ? 1 : 0);

  const clearFilters = () => {
    setSearch("");
    setCity("");
    setVehicleFilter("");
  };

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (q) {
        const match =
          (g?.name || "").toLowerCase().includes(q) ||
          (g?.code || "").toLowerCase().includes(q) ||
          String(g?.id || "").includes(q);
        if (!match) return false;
      }

      const vehicles = countVehicles(g);
      if (vehicleFilter === "with" && vehicles === 0) return false;
      if (vehicleFilter === "without" && vehicles > 0) return false;

      return true;
    });
  }, [groups, search, vehicleFilter, dailyPackages, subPackages]);

  const sortValue = (g, key) => {
    switch (key) {
      case "vehicles":
        return countVehicles(g);
      case "daily":
        return countDailyPackages(g);
      case "subscription":
        return countSubscriptionPackages(g);
      case "cityId":
        return cities[g?.cityId] || String(g?.cityId ?? "");
      case "securityDeposit":
        return Number(g?.securityDeposit ?? 0);
      case "id":
        return Number(g?.id ?? 0);
      default:
        return (g?.[key] ?? "").toString().toLowerCase();
    }
  };

  const sortedGroups = useMemo(() => {
    const dir = order === "asc" ? 1 : -1;
    return [...filteredGroups].sort((a, b) => {
      const av = sortValue(a, orderBy);
      const bv = sortValue(b, orderBy);
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredGroups,
    orderBy,
    order,
    countDailyPackages,
    countSubscriptionPackages,
  ]);

  const paginatedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedGroups.slice(start, start + rowsPerPage);
  }, [sortedGroups, page, rowsPerPage]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrder("asc");
    }
    setPage(0);
  };

  // --- Create new group ---

  const handleNewGroupChange = (field, value) => {
    setNewGroup((prev) => ({ ...prev, [field]: value }));
  };

  const resetCreateForm = () => {
    setNewGroup(emptyNewGroup);
    setOpenCreateModal(false);
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) return toast.error("Group name is required");
    if (!newGroup.code.trim()) return toast.error("Group code is required");
    if (newGroup.cityId === "") return toast.error("City is required");

    const payload = {
      name: newGroup?.name.trim(),
      code: newGroup?.code.trim(),
      cityId: Number(newGroup?.cityId),
      hubId : Number(newGroup?.hubId),
      seatingCapacity: newGroup?.seatingCapacity
        ? Number(newGroup.seatingCapacity)
        : 0,
      securityDeposit: newGroup?.securityDeposit
        ? Number(newGroup?.securityDeposit)
        : 0,
    };

    setCreatingGroup(true);
    try {
      const response = await fetch(`${apiUrl}/api/vehiclegroups`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data?.statusCode === "0100") {
        toast.success("Vehicle group created successfully");
        resetCreateForm();
        getVehicleGroups(city);
      } else {
        toast.error(data?.msg || data?.message || "Failed to create group");
      }
    } catch (error) {
      console.error("Error creating vehicle group:", error);
      toast.error("Error creating vehicle group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDownloadDailyReport = async () => {
    if (city === "") {
      return toast.error("Select a city to download the daily rental report", {
        position: "top-center",
      });
    }
    try {
      const response = await fetch(
        `${apiUrl}/api/vehiclegroups/fetch-report?cityId=${city}`,
        { method: "GET", headers: authHeaders },
      );
      const data = await response.json();
      const downloadUrl = data?.dataValues?.Location;
      if (data?.statusCode !== "0100" || !downloadUrl) {
        return toast.error(
          data?.msg || data?.message || "Failed to generate report",
          {
            position: "top-center",
          },
        );
      }
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading daily rental report:", error);
      toast.error("Error downloading daily rental report", {
        position: "top-center",
      });
    }
  };

  const handleDownloadSubscriptionReport = async () => {
    if (city === "") {
      return toast.error(
        "Select a city to download the subscription rental report",
        {
          position: "top-center",
        },
      );
    }
    try {
      const response = await fetch(
        `${apiUrl}/api/vehiclegroups/fetch-subscription-rental-report?cityId=${city}`,
        { method: "GET", headers: authHeaders },
      );
      const data = await response.json();
      const downloadUrl = data?.dataValues?.Location;
      if (data?.statusCode !== "0100" || !downloadUrl) {
        return toast.error(
          data?.msg || data?.message || "Failed to generate report",
          {
            position: "top-center",
          },
        );
      }
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading subscription rental report:", error);
      toast.error("Error downloading subscription rental report", {
        position: "top-center",
      });
    }
  };

  const countCell = (value, tooltip, onClick) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          fontVariantNumeric: "tabular-nums",
          color: value ? "text.primary" : "text.disabled",
        }}
      >
        {value}
      </Typography>
      <Tooltip title={tooltip}>
        <IconButton size="small" onClick={onClick}>
          <InfoOutlinedIcon sx={{ fontSize: 17 }} color="primary" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ p: 3, bgcolor: "#fff", borderRadius: 3, mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Vehicle groups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loadingGroups
              ? "Loading…"
              : `${sortedGroups.length} of ${groups.length} groups`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleDownloadDailyReport}
            sx={{
              bgcolor: "#1565c0",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              "&:hover": { bgcolor: "#0d47a1" },
            }}
          >
            Daily Rentals Report
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleDownloadSubscriptionReport}
            sx={{
              bgcolor: "#1565c0",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              "&:hover": { bgcolor: "#0d47a1" },
            }}
          >
            Subscription Rentals Report
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={() => setOpenCreateModal(true)}
            sx={{
              bgcolor: "#1565c0",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              "&:hover": { bgcolor: "#0d47a1" },
            }}
          >
            Create new group
          </Button>
        </Stack>
      </Stack>

      {/* Filter bar */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: "#fafbfc" }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            label="Search name, code or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, bgcolor: "#fff" }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            sx={{ minWidth: 170, bgcolor: "#fff" }}
            size="small"
          >
            <MenuItem value="">All cities</MenuItem>
            {Object.entries(cities).map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Vehicles"
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            sx={{ minWidth: 170, bgcolor: "#fff" }}
            size="small"
          >
            {vehicleFilterOptions.map((opt) => (
              <MenuItem key={opt.label} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              disabled={!filtersActive}
              onClick={clearFilters}
              sx={{ textTransform: "none" }}
            >
              Clear filters
            </Button>
          </Badge>
        </Box>
      </Paper>

      {loadingGroups ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map((cell) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                        whiteSpace: "nowrap",
                      }}
                      sortDirection={orderBy === cell.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === cell.id}
                        direction={orderBy === cell.id ? order : "asc"}
                        onClick={() => handleSort(cell.id)}
                      >
                        {cell.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedGroups.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={headCells.length}
                      align="center"
                      sx={{ py: 6 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No groups match the current filters.
                      </Typography>
                      {filtersActive && (
                        <Button
                          size="small"
                          onClick={clearFilters}
                          sx={{ mt: 1, textTransform: "none" }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedGroups.map((g) => (
                    <TableRow
                      key={g.id}
                      hover
                      sx={{ "&:nth-of-type(odd)": { bgcolor: "#fcfcfd" } }}
                    >
                      <TableCell>
                        <Chip
                          label={g.id}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {g.name || "-"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {vehicleTypeList[g.vehicleType || "-"] || "-"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {g.code || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cities[g.cityId] || g.cityId || "-"}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {g.securityDeposit ?? 0}
                      </TableCell>
                      <TableCell>
                        {countCell(
                          countVehicles(g),
                          "Click to view vehicles",
                          () => handleRowClick(g),
                        )}
                      </TableCell>
                      <TableCell>
                        {countCell(
                          countDailyPackages(g),
                          "Click to view daily rental package details",
                          () => handleOpenPackages(g, "daily"),
                        )}
                      </TableCell>
                      <TableCell>
                        {countCell(
                          countSubscriptionPackages(g),
                          "Click to view subscription package details",
                          () => handleOpenPackages(g, "subscription"),
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <TablePagination
            component="div"
            count={sortedGroups.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) =>
              setRowsPerPage(parseInt(e.target.value, 10))
            }
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Vehicles under group modal */}
      <Modal open={openModal} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {selectedGroup?.name || `Group ${selectedGroup?.id}`}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Group ID {selectedGroup?.id} · Code {selectedGroup?.code || "-"} ·
            City {cities[selectedGroup?.cityId] || selectedGroup?.cityId || "-"}
          </Typography>

          {loadingVehicles ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : groupVehicles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No vehicles found for this group.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      License plate
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Make</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Variant</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Odometer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Subscription Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupVehicles.map((v) => (
                    <TableRow key={v.id} hover>
                      <TableCell>{v.id}</TableCell>
                      <TableCell>{v.name || "-"}</TableCell>
                      <TableCell>{vehicleTypeList[v.vehicleType || "-"] || "-"}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {v.licensePlate || "-"}
                          {v.licensePlate && (
                            <Tooltip title="Copy license plate">
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleCopyPlate(e, v.licensePlate)
                                }
                              >
                                <ContentCopyIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{vehicleStatus[v.status] || "-"}</TableCell>
                      <TableCell>{v.vehicleMakeText || "-"}</TableCell>
                      <TableCell>{v.vehicleModelText || "-"}</TableCell>
                      <TableCell>{v.vehicleVariantText || "-"}</TableCell>
                      <TableCell>{v.currentOdometer ?? "-"}</TableCell>
                      <TableCell className={`${v.isSubscriptionEnabled ? "text-green-600" : "text-red-600"}`}>{v.isSubscriptionEnabled ? "True" : "False" ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Modal>

      {/* Package details modal (daily / subscription) */}
      <Modal open={packageModal.open} onClose={handleClosePackages}>
        <Box sx={packageModalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {packageModal.kind === "daily"
                ? "Daily rental packages"
                : "Subscription rental packages"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {packageRows?.length > 0 && packageModal?.kind === "subscription" ? <></> : (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={
                    !!editingPackageId ||
                    loadingPackages ||
                    (packageModal.kind === "subscription" &&
                      !subscriptionPackageAddAllowed)
                  }
                  onClick={startAddPackage}
                  sx={{
                    bgcolor: "#1565c0",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    "&:hover": { bgcolor: "#0d47a1" },
                  }}
                >
                  Add package
                </Button>
              )}
              <IconButton size="small" onClick={handleClosePackages}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {packageGroup?.name || `Group ${packageGroup?.id}`} · Group ID{" "}
            {packageGroup?.id} · City{" "}
            {cities[packageGroup?.cityId] || packageGroup?.cityId || "-"}
            {!loadingPackages && ` · ${packageRows.length} package(s)`}
          </Typography>
          {packageModal.kind === "subscription" &&
            !loadingPackages &&
            !subscriptionPackageAddAllowed && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {subscriptionVehicleCount === 0
                  ? "This group needs at least one vehicle with subscription enabled to add a subscription package."
                  : "At least one vehicle in this group must have subscription enabled to add a subscription package."}
              </Typography>
            )}

          {loadingPackages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : packageRows.length === 0 && !addingPackage ? (
            <Typography variant="body2" color="text.secondary">
              No packages found for this group.
            </Typography>
          ) : packageModal.kind === "daily" ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Free km</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Km / hr</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cost / km</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cost / hr</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Extra km cost
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Extra hr cost
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Weekday tariff
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Weekend tariff
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addingPackage && (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={newPackageForm.name ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange("name", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.totalFreeKm ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "totalFreeKm",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.kmPerHr ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange("kmPerHr", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.costPerKm ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange("costPerKm", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.costPerHr ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange("costPerHr", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.extraKmCost ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "extraKmCost",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.extraHrCost ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "extraHrCost",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.weekdayTariff ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "weekdayTariff",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.weekendTariff ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "weekendTariff",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={savingPackage}
                            onClick={saveNewPackage}
                          >
                            Save
                          </Button>
                          <Button size="small" onClick={cancelAddPackage}>
                            Cancel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                  {packageRows?.map((p) => {
                    const isEditing = editingPackageId === p.id;
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.id}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={packageEditForm.name ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange("name", e.target.value)
                              }
                            />
                          ) : (
                            p.name || "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.totalFreeKm ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "totalFreeKm",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.totalFreeKm ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.kmPerHr ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "kmPerHr",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.kmPerHr ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.costPerKm ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "costPerKm",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.costPerKm ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.costPerHr ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "costPerHr",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.costPerHr ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.extraKmCost ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "extraKmCost",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.extraKmCost ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.extraHrCost ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "extraHrCost",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.extraHrCost ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.weekdayTariff ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "weekdayTariff",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.weekdayTariff ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.weekendTariff ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "weekendTariff",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.weekendTariff ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={savingPackage}
                                onClick={saveEditPackage}
                              >
                                Save
                              </Button>
                              <Button size="small" onClick={cancelEditPackage}>
                                Cancel
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              disabled={
                                addingPackage ||
                                (editingPackageId && editingPackageId !== p.id)
                              }
                              onClick={() => startEditPackage(p)}
                            >
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 1100,
                  tableLayout: "fixed",
                  "& th, & td": {
                    textAlign: "center",
                    verticalAlign: "middle",
                  },
                  "& .MuiTextField-root": { width: "100%" },
                  "& .MuiInputBase-input": { textAlign: "center" },
                }}
              >
                <colgroup>
                  <col style={{ width: 60 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 85 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 120 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ bgcolor: "#f5f7fa", fontWeight: 700 }}>
                      ID
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ bgcolor: "#f5f7fa", fontWeight: 700 }}>
                      Name
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ bgcolor: "#f5f7fa", fontWeight: 700 }}>
                      Min days
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        bgcolor: "#fff",
                        color: "#000",
                        fontWeight: 700,
                        border: "2.5px solid #000",
                      }}
                    >
                      Price per day
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        bgcolor: "#fff",
                        color: "#000",
                        fontWeight: 700,
                        border: "2.5px solid #000",
                      }}
                    >
                      KM per day
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      align="center"
                      sx={{
                        bgcolor: "#fff",
                        color: "#000",
                        fontWeight: 700,
                        border: "2.5px solid #000",
                      }}
                    >
                      Extra Charges per day
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{ bgcolor: "#f5f7fa", fontWeight: 700 }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>7 Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>15 Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>30 Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>7 Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>15 Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>30 Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Per Km</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addingPackage && (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={newPackageForm.packageName ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "packageName",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          disabled={true}
                          value={MINIMUM_DAY_COUNT}
                          // onChange={(e) =>
                          //   handleNewPackageChange(
                          //     "minDayCount",
                          //     e.target.value,
                          //   )
                          // }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.dailyRateWeekly ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "dailyRateWeekly",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.dailyRateFortnightly ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "dailyRateFortnightly",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.dailyRateMonthly ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "dailyRateMonthly",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.kmPerDayWeekly ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "kmPerDayWeekly",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.kmPerDayFortnightly ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "kmPerDayFortnightly",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.kmPerDayMonthly ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "kmPerDayMonthly",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.extraKmCost ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "extraKmCost",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      {/* <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={newPackageForm.extraHrCost ?? ""}
                          onChange={(e) =>
                            handleNewPackageChange(
                              "extraHrCost",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell> */}
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={savingPackage}
                            onClick={saveNewPackage}
                          >
                            Save
                          </Button>
                          <Button size="small" onClick={cancelAddPackage}>
                            Cancel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                  {packageRows.map((p) => {
                    const isEditing = editingPackageId === p.id;
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.id}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={packageEditForm.packageName ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "packageName",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            p.packageName || "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              disabled={true}
                              value={MINIMUM_DAY_COUNT}
                              // onChange={(e) =>
                              //   handlePackageFieldChange(
                              //     "minDayCount",
                              //     e.target.value,
                              //   )
                              // }
                            />
                          ) : (
                            (p.minDayCount ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.dailyRateWeekly ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "dailyRateWeekly",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.dailyRateWeekly ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.dailyRateFortnightly ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "dailyRateFortnightly",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.dailyRateFortnightly ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.dailyRateMonthly ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "dailyRateMonthly",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.dailyRateMonthly ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.kmPerDayWeekly ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "kmPerDayWeekly",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.kmPerDayWeekly ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.kmPerDayFortnightly ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "kmPerDayFortnightly",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.kmPerDayFortnightly ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.kmPerDayMonthly ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "kmPerDayMonthly",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.kmPerDayMonthly ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={packageEditForm.extraKmCost ?? ""}
                              onChange={(e) =>
                                handlePackageFieldChange(
                                  "extraKmCost",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            (p.extraKmCost ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={savingPackage}
                                onClick={saveEditPackage}
                              >
                                Save
                              </Button>
                              <Button size="small" onClick={cancelEditPackage}>
                                Cancel
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              disabled={
                                addingPackage ||
                                (editingPackageId && editingPackageId !== p.id)
                              }
                              onClick={() => startEditPackage(p)}
                            >
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Modal>

      {/* Create new group modal */}
      <Modal open={openCreateModal} onClose={resetCreateForm}>
        <Box sx={createGroupModalStyle}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Create new group
            </Typography>
            <IconButton size="small" onClick={resetCreateForm}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Stack spacing={2}>
            <TextField
              label="Group name"
              required
              value={newGroup.name}
              onChange={(e) => handleNewGroupChange("name", e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Group code"
              required
              value={newGroup.code}
              onChange={(e) => handleNewGroupChange("code", e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              select
              label="City"
              required
              value={newGroup.cityId}
              onChange={(e) => handleNewGroupChange("cityId", e.target.value)}
              size="small"
              fullWidth
            >
              {Object.entries(cities).map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Hub"
              required
              value={newGroup.hubId}
              onChange={(e) => handleNewGroupChange("hubId", e.target.value)}
              size="small"
              fullWidth
            >
              {getAllHubsListData?.map((hub) => {
  return (
    <MenuItem key={hub?.id} value={hub?.id}>
      {hub?.hubName}
    </MenuItem>
  );
})}
            </TextField>
            <TextField
              label="Seating capacity"
              type="number"
              value={newGroup.seatingCapacity}
              onChange={(e) =>
                handleNewGroupChange("seatingCapacity", e.target.value)
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Security deposit"
              type="number"
              value={newGroup.securityDeposit}
              onChange={(e) =>
                handleNewGroupChange("securityDeposit", e.target.value)
              }
              size="small"
              fullWidth
            />

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ pt: 1 }}
            >
              <Button onClick={resetCreateForm} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={creatingGroup}
                onClick={handleCreateGroup}
                sx={{
                  bgcolor: "#1565c0",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  "&:hover": { bgcolor: "#0d47a1" },
                }}
              >
                {creatingGroup ? "Creating..." : "Create group"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
}
